import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthShell, AuthHeading, AuthField, AuthAlert } from "../components/AuthShell";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

export default function Signup() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setDone(data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell>
        <div className="space-y-6" data-testid="signup-success">
          <CheckCircle2 className="h-10 w-10 text-emerald-400" />
          <AuthHeading title="Request sent" subtitle={done} />
          <p className="text-sm text-slate-400">The owner will assign you to your depot. You'll be able to sign in once approved.</p>
          <Button asChild className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="signup-back-to-login">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={submit} className="space-y-8" data-testid="signup-form">
        <AuthHeading title="Request access" subtitle="Create your employee account. The owner approves it and assigns your depot." />
        <div className="space-y-4">
          <AuthField id="name" label="Full name" autoComplete="name" value={name} onChange={setName} testid="signup-name-input" />
          <AuthField id="email" label="Email" type="email" autoComplete="email" value={email} onChange={setEmail} testid="signup-email-input" />
          <AuthField id="password" label="Password (min 8 characters)" type="password" autoComplete="new-password" minLength={8} value={password} onChange={setPassword} testid="signup-password-input" />
          <AuthField id="confirm" label="Confirm password" type="password" autoComplete="new-password" minLength={8} value={confirm} onChange={setConfirm} testid="signup-confirm-input" />
          {error && <AuthAlert testid="signup-error">{error}</AuthAlert>}
        </div>
        <Button type="submit" disabled={busy} className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="signup-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send request
        </Button>
        <p className="text-sm text-slate-400">
          Already have an account? <Link to="/login" className="text-amber-400 hover:underline font-medium" data-testid="signup-login-link">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
